// @react
import React, { ReactElement, useState, useContext } from 'react'
// @types
import {
  GetOrdersQuery,
  GetOrdersQueryVariables,
  UserUpdateOrderAutorenewalMutation,
  UserUpdateOrderAutorenewalMutationVariables,
} from 'lib/graphql/graphqlTypes'
import { PropsType as CardProps } from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Orders'
import { PropsType as CardItemProps } from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Orders/LineItem'
import { SnackbarTypes } from '@bit/atd.web.snackbar'
import { AutoRenewalSubmitData } from 'modules/profile/components/Membership/Form'
// @context
import { SnackbarContext } from 'modules/common/context/snackbar'
// @graphQL
import { useMutation, useQuery } from '@apollo/client'
import { GET_ORDERS } from 'graphql/queries/order.query'
import { USER_UPDATE_ORDER_AUTORENEWAL } from 'graphql/mutations/user.mutation'
// @libraries
import { vsprintf } from 'sprintf-js'
import config from 'constants/config'
import strings from 'lib/constants/strings'
import Helper from 'utils/Helper.class'
// @components
import Spacer from '@bit/atd.web.spacer'
import OrderCard from '__COMPONENTS_TO_MIGRATE/CardNew/Variants/Orders'
import ConditionalLoader from '@bit/atd.web.loaders.conditional-loader'
// @module components
import usePagination from 'modules/common/hooks/usePagination'
import PaginationWrapper from 'modules/common/components/PaginationWrapper'
import QueryError from 'modules/common/components/Errors/QueryError'
import AutoRenewForm from 'modules/profile/components/Membership/Form/AutoRenew'
import Modal from '@bit/atd.web.modal'
import Button from '@bit/atd.web.button'
import { MembershipContext } from '../index'

type PropsType = {
  setCount: (count: number) => void
  searchKeyword: string
  timeLimit?: number
}

const AllOrders = (props: PropsType): ReactElement => {
  /****************************************
   *  PROPS
   ****************************************/

  const { setCount, timeLimit, searchKeyword } = props

  /****************************************
   *  STATE
   ****************************************/

  const [membershipContext, setMembershipContext] = useState<MembershipContext>(
    null,
  )
  const [renewModal, setRenewModal] = useState<boolean>(false)
  const [renewEndedModal, setRenewEndedModal] = useState<boolean>(false)
  const [isMutationSubmitting, setIsSubmitting] = useState<boolean>(false)

  /****************************************
   *  HOOKS
   ****************************************/

  const snackbar = useContext(SnackbarContext)
  const { Pagination, paginationProps } = usePagination()

  /****************************************
   *  GQL HOOKS
   ****************************************/

  const { loading: isLoading, error: isError, data, refetch } = useQuery<
    GetOrdersQuery,
    GetOrdersQueryVariables
  >(GET_ORDERS, {
    variables: {
      input: {
        page: paginationProps.currentPage,
        pageSize: config.pageSize,
        filters: {
          dateRange: timeLimit,
          search: searchKeyword,
        },
      },
    },
  })

  const count = data?.orders?.meta?.count
  setCount(count || 0)

  const [updateUserOrderAutoRenewal] = useMutation<
    UserUpdateOrderAutorenewalMutation,
    UserUpdateOrderAutorenewalMutationVariables
  >(USER_UPDATE_ORDER_AUTORENEWAL)

  /****************************************
   *  FUNCTION
   ****************************************/

  // on Auto-Renew Toggle Handlers

  const onRenewHandler = async (
    autoRenew: CardItemProps['autoRenew'],
    orderNumber: CardProps['orderNumber'],
    orderLineNumber: CardItemProps['orderLineNumber'],
    title: CardItemProps['title'],
  ) => {
    if (!autoRenew) {
      setMembershipContext({
        title: title,
        orderNumber: orderNumber,
        orderLineNumber: orderLineNumber,
      })

      setRenewModal(true)
    } else {
      await userOrderAutorenewalUpdateHandler(
        {
          orderNumber: orderNumber,
          // @ts-ignore
          orderLineNumber: orderLineNumber,
        },
        false,
      )
    }
  }

  // Turn off state
  const onRenewEndedConfirmHandler = () => {
    setRenewEndedModal(false)
  }

  const userOrderAutorenewalUpdateHandler = async (
    data: AutoRenewalSubmitData,
    targetAutorenewal: boolean,
  ): Promise<boolean | void> => {
    // CC type codes AE,DS,MC,VI

    /**
     * @todo revise this once endpoint reviewed for autorenewal cancel
     * endpoint returns error currently if no CC data is passed in when cancelling
     * autorenewal
     */

    const input = targetAutorenewal
      ? {
          orderNumber: data.orderNumber,
          orderLineNumber: data.orderLineNumber,
          isAutoRenewal: true,
          creditCardType: data.creditCardType,
          creditCardNumber: data.creditCardNumber,
          creditCardSecurityCode: data.creditCardSecurityCode,
          nameOnCard: data.nameOnCard,
          expirationYear: data.expirationYear,
          expirationMonth: data.expirationMonth,
        }
      : {
          orderNumber: data.orderNumber,
          orderLineNumber: data.orderLineNumber,
          isAutoRenewal: false,
        }

    const { errors: mutationErrors } = await updateUserOrderAutoRenewal({
      variables: {
        input,
      },
    })

    setIsSubmitting(false)

    if (!mutationErrors) {
      snackbar.setMessage({
        type: SnackbarTypes.SUCCESS,
        message: vsprintf(strings.SUCCESS.GENERIC_VARIABLE_UPDATE_SUCCESS, [
          'Membership Autorenewal',
        ]),
        show: true,
      })
      if (!targetAutorenewal) setRenewEndedModal(true)
      await refetch()
      return true
    } else {
      snackbar.setMessage({
        type: SnackbarTypes.ERROR,
        message: strings.ERRORS.GENERIC_UPDATE_ERROR,
        show: true,
      })
      console.log('mutation error', mutationErrors)
    }
  }

  /****************************************
   *  RENDERS
   ****************************************/

  if (isError || (!data && !isLoading)) {
    return <QueryError />
  }

  const renderContent = () => {
    if (data?.orders?.data && data?.orders?.data?.length > 0) {
      return (
        <>
          {data?.orders.data.map((item, index) => {
            const totalItems = data?.orders.data.length
            const lineItems: CardProps['lineItems'] = item?.lineItems?.map(
              (lineItem) => {
                return {
                  url: lineItem.itemUrl,
                  imageURL: lineItem?.imageUrl || '',
                  downloadURL: lineItem?.downloadUrl || '',
                  title: lineItem?.title,
                  type: lineItem?.type,
                  price: lineItem?.price,
                  autoRenew: lineItem?.autoRenew,
                  orderLineNumber: lineItem?.orderLineNumber,
                }
              },
            )

            return (
              <React.Fragment key={`${item.__typename}_${item.id}`}>
                <OrderCard
                  date={Helper.smallDateFormatter(item?.date)}
                  orderNumber={item?.orderNumber}
                  total={item?.totalPrice}
                  shippedTo={item?.shippedTo || ''}
                  receiptUrl={item?.receiptUrl || ''}
                  lineItems={lineItems}
                  onAutoRenewChange={onRenewHandler}
                />
                {index + 1 !== totalItems && <Spacer size={24} fullWidth />}
              </React.Fragment>
            )
          })}

          <PaginationWrapper>
            <Pagination
              {...paginationProps}
              count={data?.orders.meta?.totalPages}
            />
          </PaginationWrapper>

          {/* Auto-Renew Payment Form Modal */}
          <AutoRenewForm
            membershipContext={membershipContext}
            renewModal={renewModal}
            setRenewModal={setRenewModal}
            renewalHandler={userOrderAutorenewalUpdateHandler}
            isMutationSubmitting={isMutationSubmitting}
          />

          {/* Auto-Renew Ended Modal */}
          <Modal
            forceAction
            footerNoSeparator
            headerNoSeparator
            open={renewEndedModal}
            size="medium"
            title={strings.PROFILE.ORDERS.AUTO_RENEWAL_ENDED}
          >
            <p>{strings.PROFILE.ORDERS.AUTO_RENEWAL_DISCONTINUED}</p>
            <p>{membershipContext?.title}</p>
            <p>{strings.PROFILE.ORDERS.AUTO_RENEWAL_ENABLE_CTA}</p>
            <Button
              variant="primary"
              size="medium"
              onClick={onRenewEndedConfirmHandler}
            >
              {strings.PROFILE.ORDERS.OKAY}
            </Button>
          </Modal>
        </>
      )
    }
    return (
      <div>
        <h3>{strings.PROFILE.ORDERS.NO_ORDERS}</h3>
      </div>
    )
  }

  return (
    <>
      <ConditionalLoader condition={!isLoading}>
        {renderContent()}
      </ConditionalLoader>
    </>
  )
}

export default AllOrders
